import { useRef, useState } from 'react';
import { sendAICommand } from '../services/api';
import { useDemo } from './DemoModal';
import { buildDeliveryOrderPdf } from '../utils/pdf';
import { MATERIAL_NAME, MATERIAL_UNIT, type AIAction, type Yard } from '../types';
import { todayISO } from '../utils/format';

interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  actions?: AIAction[];
}

const MAX_MESSAGES = 10;
const MORE_DEMOS_MAILTO =
  'mailto:trent@performwiser.com' +
  '?subject=' +
  encodeURIComponent('More demos like Cornerstone') +
  '&body=' +
  encodeURIComponent(
    "Hi Trent,\n\nI just explored the Cornerstone delivered-pricing demo and I'd love to see more " +
      'demos like this for my business. Could we set up a time to walk through a few?\n\nThanks!'
  );

export default function AIChat({ yards }: { yards: Yard[] }) {
  const { showDemo } = useDemo();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(new Set());
  const [sentCount, setSentCount] = useState<number>(() => {
    const v = Number(localStorage.getItem('cs-ai-count'));
    return Number.isFinite(v) ? v : 0;
  });
  const listRef = useRef<HTMLDivElement>(null);
  const atLimit = sentCount >= MAX_MESSAGES;

  const markApplied = (k: string) => setAppliedKeys((s) => new Set(s).add(k));

  const send = async () => {
    const text = input.trim();
    if (!text || busy || atLimit) return;
    const next: ChatMsg[] = [...msgs, { role: 'user', content: text }];
    setMsgs(next);
    setInput('');
    setBusy(true);
    setSentCount((c) => {
      const n = c + 1;
      localStorage.setItem('cs-ai-count', String(n));
      return n;
    });
    try {
      const resp = await sendAICommand(next.map((m) => ({ role: m.role, content: m.content })));
      setMsgs((m) => [...m, { role: 'assistant', content: resp.reply || ' ', actions: resp.actions }]);
      setTimeout(() => listRef.current?.scrollTo(0, listRef.current.scrollHeight), 50);
    } catch (e: any) {
      setMsgs((m) => [...m, { role: 'assistant', content: `Error: ${e?.message ?? 'failed'}` }]);
    } finally {
      setBusy(false);
    }
  };

  const applyPrice = (a: Extract<AIAction, { type: 'update_price' }>, key: string) => {
    const yardName = yards.find((y) => y.yard_id === a.yard_id)?.name ?? a.yard_id;
    showDemo({
      title: 'Apply price update',
      body: (
        <>
          In the live app this sets {MATERIAL_NAME[a.material] ?? a.material} at {yardName} to{' '}
          ${a.new_price}, saves it to the pricing tables, and recomputes delivered pricing.
        </>
      ),
    });
    markApplied(key);
  };

  const applyOrder = (a: Extract<AIAction, { type: 'create_order' }>, key: string) => {
    showDemo({
      title: 'Log delivery order',
      body: (
        <>
          In the live app this creates a delivery order for {a.quantity}{' '}
          {MATERIAL_UNIT[a.material] ?? 'units'} of {MATERIAL_NAME[a.material] ?? a.material} to{' '}
          {a.site_address}, assigns an order number, and adds it to the Orders board.
        </>
      ),
    });
    markApplied(key);
  };

  const pdfForOrder = (a: Extract<AIAction, { type: 'create_order' }>) => {
    const yard = yards.find((y) => y.yard_id === a.yard_id);
    buildDeliveryOrderPdf({
      orderNo: `DRAFT-${todayISO()}`,
      date: todayISO(),
      yardName: yard?.name ?? a.yard_id,
      yardAddress: [yard?.address, yard?.city, yard?.state].filter(Boolean).join(', ') || '—',
      material: MATERIAL_NAME[a.material] ?? a.material,
      quantity: a.quantity,
      unit: MATERIAL_UNIT[a.material] ?? 'unit',
      deliveredPerUnit: a.delivered_price_per_unit,
      deliveredTotal: a.delivered_total,
      siteAddress: a.site_address,
    });
  };

  const renderAction = (a: AIAction, mi: number, ai: number) => {
    const key = `${mi}:${ai}`;
    const applied = appliedKeys.has(key);
    if (a.type === 'update_price') {
      return (
        <div className="ai-action-card" key={key}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{a.display}</div>
          <button className="btn btn-sm btn-primary" disabled={applied} onClick={() => applyPrice(a, key)}>
            {applied ? '✓ Applied' : 'Apply'}
          </button>
        </div>
      );
    }
    if (a.type === 'create_order') {
      return (
        <div className="ai-action-card" key={key}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{a.display}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm btn-primary" disabled={applied} onClick={() => applyOrder(a, key)}>
              {applied ? '✓ Logged' : 'Apply / Log order'}
            </button>
            <button className="btn btn-sm" onClick={() => pdfForOrder(a)}>
              PDF
            </button>
          </div>
        </div>
      );
    }
    if (a.type === 'clarify') {
      return (
        <div className="ai-action-card" key={key}>
          <span className="muted">{a.display}</span>
        </div>
      );
    }
    return (
      <div className="ai-action-card" key={key}>
        <span className="muted">Feature request noted: {a.summary}</span>
      </div>
    );
  };

  if (!open) {
    return (
      <button
        className="ai-bubble"
        data-tour="ai"
        title="Ask the assistant"
        onClick={() => setOpen(true)}
      >
        ✦
      </button>
    );
  }

  return (
    <div className="ai-panel">
      <div className="home-module-header">
        <span>Assistant</span>
        <button className="btn btn-sm" onClick={() => setOpen(false)}>
          ✕
        </button>
      </div>
      <div ref={listRef} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', padding: 4 }}>
        {msgs.length === 0 && (
          <p className="muted" style={{ padding: 10, fontSize: 13 }}>
            Try: “20 yards hardwood mulch to 123 Oak St” or “set topsoil at Maple Yard to 42”.
          </p>
        )}
        {msgs.map((m, mi) => (
          <div key={mi} style={{ display: 'flex', flexDirection: 'column' }}>
            <div className={m.role === 'user' ? 'ai-msg-user' : 'ai-msg-bot'}>{m.content}</div>
            {m.actions?.map((a, ai) => renderAction(a, mi, ai))}
          </div>
        ))}
      </div>
      {atLimit ? (
        <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
          <p style={{ margin: '0 0 10px', fontWeight: 600 }}>
            Sorry! This demo has a limit to 10 messages.
          </p>
          <a className="btn btn-primary btn-sm" href={MORE_DEMOS_MAILTO}>
            See More Demos
          </a>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: '1px solid var(--border)' }}>
          <input
            className="input"
            placeholder={`Message… (${MAX_MESSAGES - sentCount} left)`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
          />
          <button className="btn btn-primary" onClick={send} disabled={busy}>
            {busy ? '…' : 'Send'}
          </button>
        </div>
      )}
    </div>
  );
}
